import * as React from "react";

/**
 * Translate Radix's `asChild` into Base UI's `render`.
 *
 * The two props mean the same thing from a caller's side: "use my element as
 * the component's element rather than rendering your own". Radix takes the
 * single child and merges props into it; Base UI takes that element as `render`
 * instead. So the translation is simply to hand the child over.
 *
 * This exists so the Radix→Base UI migration doesn't become a repo-wide sweep.
 * `asChild` appears at 825 call sites across the app; absorbing it in the
 * `@erp/ui` wrappers keeps each component's migration to a handful of files and
 * lets a mechanical sweep to `render` follow later as its own change. It is an
 * adapter, not drift.
 *
 * An explicit `render` always wins, so a caller can opt into the Base UI spelling
 * at any time without the wrapper second-guessing them.
 *
 * Returns `undefined` when there is nothing to translate — `asChild` was not
 * passed, or it was passed with children that aren't a single element (a bare
 * string, a fragment of several nodes). Callers should then render `children`
 * normally, which is what Radix does in the same situation.
 */
export function resolveRender<TRender>(
  asChild: boolean | undefined,
  render: TRender | undefined,
  children: React.ReactNode,
): TRender | React.ReactElement | undefined {
  if (render) {
    return render;
  }
  return asChild && React.isValidElement(children) ? children : undefined;
}
